import { useState } from "react";
import { ChevronDown, ChevronRight, AlertTriangle, ShieldOff, ShieldCheck, ShieldAlert, Anchor, TrendingDown as ArrowTrendDown, Layers, Sparkles } from "lucide-react";
import { DirectionBadge, StatusBadge, ConfidenceBadge } from "@/components/Badges";

const PATTERN_COLORS = {
  FVG: { c: "#3b82f6", b: "rgba(59, 130, 246, 0.15)" },
  BOS: { c: "#10b981", b: "rgba(16, 185, 129, 0.15)" },
  CHoCH: { c: "#f59e0b", b: "rgba(245, 158, 11, 0.15)" },
  LiquiditySweep: { c: "#a78bfa", b: "rgba(167, 139, 250, 0.15)" },
};

const PatternBadge = ({ pattern }) => {
  const p = PATTERN_COLORS[pattern] || PATTERN_COLORS.FVG;
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-mono font-bold border uppercase"
      style={{ background: p.b, borderColor: p.c, color: p.c }}
    >
      {pattern === "LiquiditySweep" ? "SWEEP" : pattern}
    </span>
  );
};

// Phase 2.1 — Trade Management inline badges (BE / TS / PC).
const TmBadges = ({ exec, index }) => {
  const tm = exec?.trade_management || {};
  const beOn = !!tm.break_even_enabled;
  const tsOn = !!tm.trailing_stop_enabled;
  const pcOn = !!tm.partial_close_enabled && (tm.partial_close_levels || []).length > 0;
  if (!beOn && !tsOn && !pcOn) return null;
  const tip = [
    beOn && `BE: move SL to entry +${tm.break_even_offset_pips}p when +${tm.break_even_trigger_pips}p in profit`,
    tsOn && `TS: arm @ +${tm.trailing_stop_trigger_pips}p, trail by ${tm.trailing_stop_distance_pips}p`,
    pcOn && `PC: ${(tm.partial_close_levels || []).map((lv) => `${lv.close_pct}% @ +${lv.trigger_pips}p`).join(", ")}`,
  ].filter(Boolean).join("\n");
  return (
    <div className="flex items-center gap-1 flex-wrap" title={tip} data-testid={`tm-badges-${index}`}>
      {beOn && (
        <span data-testid={`tm-be-badge-${index}`} className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-[9px] font-mono font-bold border border-[#10b981]/70 text-[#10b981] bg-[#10b981]/10">
          <Anchor className="w-2.5 h-2.5" /> BE
        </span>
      )}
      {tsOn && (
        <span data-testid={`tm-ts-badge-${index}`} className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-[9px] font-mono font-bold border border-[#3b82f6]/70 text-[#3b82f6] bg-[#3b82f6]/10">
          <ArrowTrendDown className="w-2.5 h-2.5" /> TS
        </span>
      )}
      {pcOn && (
        <span data-testid={`tm-pc-badge-${index}`} className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-[9px] font-mono font-bold border border-[#f59e0b]/70 text-[#f59e0b] bg-[#f59e0b]/10">
          <Layers className="w-2.5 h-2.5" /> PC×{(tm.partial_close_levels || []).length}
        </span>
      )}
    </div>
  );
};

const DECISION_STYLE = {
  APPROVED: { c: "#10b981", b: "rgba(16, 185, 129, 0.15)", Icon: ShieldCheck },
  ADVISORY: { c: "#f59e0b", b: "rgba(245, 158, 11, 0.15)", Icon: ShieldAlert },
  BLOCKED:  { c: "#ef4444", b: "rgba(239, 68, 68, 0.15)", Icon: ShieldOff },
};

const DecisionBadge = ({ decision, testId }) => {
  const s = DECISION_STYLE[decision] || DECISION_STYLE.ADVISORY;
  const { Icon } = s;
  return (
    <span
      data-testid={testId}
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-mono font-bold border"
      style={{ background: s.b, borderColor: s.c, color: s.c }}
    >
      <Icon className="w-3 h-3" strokeWidth={2.25} /> {decision || "—"}
    </span>
  );
};

const formatNum = (n) => {
  if (n === null || n === undefined) return "—";
  const num = Number(n);
  if (Number.isNaN(num)) return "—";
  return num.toFixed(5).replace(/0+$/, "").replace(/\.$/, "");
};

const formatTime = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
};

const formatMoney = (n) => (n === null || n === undefined ? "—" : `$${Number(n).toFixed(2)}`);

export const SignalRow = ({ signal, index }) => {
  const [open, setOpen] = useState(false);
  const rowClass = signal.direction === "BUY" ? "vx-row-buy" : "vx-row-sell";
  const news = signal.news_warning;
  const exec = signal.execution || {};
  const primaryReason = exec.decision === "BLOCKED" ? (exec.reasons || [])[0] : null;

  return (
    <>
      <tr
        className={`border-b border-[#1f2937] hover:bg-[#1f2937]/50 transition-colors ${rowClass}`}
        data-testid={`signal-row-${index}`}
      >
        <td className="py-3 pl-4 pr-2">
          <button
            onClick={() => setOpen((o) => !o)}
            className="text-[#6b7280] hover:text-white transition-colors"
            data-testid={`expand-row-${index}`}
            aria-label="Toggle reasoning"
          >
            {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>
        </td>
        <td className="py-3 px-2 font-mono text-xs text-[#9ca3af]">{formatTime(signal.created_at)}</td>
        <td className="py-3 px-2 font-mono text-xs font-semibold">{signal.symbol}</td>
        <td className="py-3 px-2 font-mono text-xs text-[#9ca3af]">{signal.timeframe}</td>
        <td className="py-3 px-2">
          <div className="flex items-center gap-1">
            <PatternBadge pattern={signal.pattern_type} />
            {signal.signal_type === "straddle_pair" && (
              <span
                data-testid={`straddle-pill-${index}`}
                title={`Straddle-lock ${signal.side?.toUpperCase()} leg · group ${signal.straddle_group_id?.slice(0, 8)}…`}
                className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-[9px] font-mono font-bold border border-[#a78bfa]/70 text-[#a78bfa] bg-[#a78bfa]/10"
              >
                <Sparkles className="w-2.5 h-2.5" /> STRADDLE
              </span>
            )}
          </div>
        </td>
        <td className="py-3 px-2"><DirectionBadge direction={signal.direction} testId={`direction-${index}`} /></td>
        <td className="py-3 px-2 font-mono text-xs text-right">{formatNum(signal.entry_price)}</td>
        <td className="py-3 px-2 font-mono text-xs text-right text-[#ef4444]/80">{formatNum(signal.stop_loss)}</td>
        <td className="py-3 px-2 font-mono text-xs text-right text-[#10b981]/80">{formatNum(signal.take_profit)}</td>
        <td className="py-3 px-2 font-mono text-xs text-right" data-testid={`lot-${index}`}>{exec.lot_size ?? "—"}</td>
        <td className="py-3 px-2 font-mono text-xs text-right text-[#f59e0b]/90" data-testid={`max-loss-${index}`}>{formatMoney(exec.expected_max_loss_usd)}</td>
        <td className="py-3 px-2 text-center">
          <div className="flex items-center justify-center gap-1">
            <ConfidenceBadge value={signal.confidence_score} testId={`confidence-${index}`} />
            {news && (
              <span title={`⚠ ${news.event_name} (${news.currency}) — confidence reduced`} data-testid={`news-warning-${index}`}>
                <AlertTriangle className="w-3.5 h-3.5 text-[#f59e0b]" />
              </span>
            )}
          </div>
        </td>
        <td className="py-3 px-2">
          <div className="flex flex-col gap-0.5 items-start">
            <DecisionBadge decision={exec.decision} testId={`decision-${index}`} />
            {signal.ati_decision?.verdict && (
              <span
                className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-mono font-bold border ${
                  signal.ati_decision.verdict === "APPROVED"
                    ? "border-[#10b981]/40 bg-[#10b981]/10 text-[#10b981]"
                    : signal.ati_decision.verdict === "REDUCED"
                    ? "border-[#f59e0b]/40 bg-[#f59e0b]/10 text-[#f59e0b]"
                    : "border-[#ef4444]/40 bg-[#ef4444]/10 text-[#ef4444]"
                }`}
                title={signal.ati_decision.narrative || ""}
                data-testid={`ati-badge-${index}`}
              >
                ATI · {signal.ati_decision.verdict}
                {signal.ati_decision.lot_multiplier < 1 && signal.ati_decision.lot_multiplier > 0 && (
                  <span className="opacity-70">×{Math.round(signal.ati_decision.lot_multiplier * 100)}%</span>
                )}
              </span>
            )}
            {primaryReason && (
              <span className="text-[9px] font-mono text-[#ef4444]/80 max-w-[160px] truncate" title={primaryReason}>
                {primaryReason}
              </span>
            )}
            <TmBadges exec={exec} index={index} />
          </div>
        </td>
        <td className="py-3 px-2"><StatusBadge status={signal.status} testId={`status-${index}`} /></td>
      </tr>
      {open && (
        <tr className="bg-[#0a0e1a]" data-testid={`reasoning-row-${index}`}>
          <td></td>
          <td colSpan={13} className="px-4 py-4 text-xs text-[#9ca3af] leading-relaxed border-b border-[#1f2937]">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <div className="vx-label mb-2">LLM Reasoning</div>
                <div>{signal.llm_reasoning || "No reasoning available."}</div>
                {news && (
                  <div className="mt-3 p-2 rounded border border-[#f59e0b]/40 bg-[#f59e0b]/10 text-[#f59e0b] text-[11px] font-mono">
                    ⚠ {news.event_name} · {news.currency} · in {news.minutes_until} min · confidence −20
                  </div>
                )}
              </div>
              <div>
                <div className="vx-label mb-2">Market Context</div>
                <dl className="grid grid-cols-2 gap-x-6 gap-y-1 font-mono text-[11px]">
                  <dt className="text-[#6b7280]">ATR</dt><dd>{formatNum(signal.atr)}</dd>
                  <dt className="text-[#6b7280]">Volume</dt><dd>{signal.volume_regime || "—"}</dd>
                  <dt className="text-[#6b7280]">Trend</dt><dd>{signal.trend_regime || "—"}</dd>
                  <dt className="text-[#6b7280]">Session</dt><dd>{signal.session || "—"}</dd>
                  {signal.structure_context?.last_swings?.length > 0 && (
                    <>
                      <dt className="text-[#6b7280] col-span-2 mt-1">Last swings</dt>
                      {signal.structure_context.last_swings.slice(-3).map((s, i) => (
                        <dd key={i} className="col-span-2">{s.label} {s.type} @ {formatNum(s.price)}</dd>
                      ))}
                    </>
                  )}
                </dl>
              </div>
              <div>
                <div className="vx-label mb-2">Risk Evaluation</div>
                <dl className="grid grid-cols-2 gap-x-6 gap-y-1 font-mono text-[11px]">
                  <dt className="text-[#6b7280]">Decision</dt><dd><DecisionBadge decision={exec.decision} /></dd>
                  <dt className="text-[#6b7280]">Lot size</dt><dd>{exec.lot_size ?? "—"}</dd>
                  <dt className="text-[#6b7280]">Risk $</dt><dd>{formatMoney(exec.risk_amount_usd)}</dd>
                  <dt className="text-[#6b7280]">Max loss $</dt><dd>{formatMoney(exec.expected_max_loss_usd)}</dd>
                  <dt className="text-[#6b7280]">SL pips</dt><dd>{exec.sl_distance_pips ?? "—"}</dd>
                  <dt className="text-[#6b7280]">Pip value</dt><dd>${exec.pip_value ?? "—"}</dd>
                  <dt className="text-[#6b7280]">Corr. group</dt><dd>{exec.correlation_group || "—"}</dd>
                  <dt className="text-[#6b7280]">Margin needed</dt><dd>{formatMoney(exec.estimated_margin_usd)}</dd>
                </dl>
                {exec.reasons?.length > 0 && (
                  <ul className="mt-2 pl-4 list-disc text-[11px] text-[#9ca3af] space-y-0.5">
                    {exec.reasons.slice(0, 4).map((r, i) => <li key={i}>{r}</li>)}
                  </ul>
                )}
                {signal.ati_decision && (
                  <div className="mt-3 pt-3 border-t border-[#1f2937]" data-testid={`ati-detail-${index}`}>
                    <div className="vx-label mb-1">ATI Decision</div>
                    <div className="text-[11px] text-[#e5e7eb] mb-2 leading-relaxed italic">
                      {signal.ati_decision.narrative}
                    </div>
                    <dl className="grid grid-cols-2 gap-x-6 gap-y-1 font-mono text-[11px]">
                      <dt className="text-[#6b7280]">Verdict</dt>
                      <dd className={
                        signal.ati_decision.verdict === "APPROVED" ? "text-[#10b981]" :
                        signal.ati_decision.verdict === "REDUCED" ? "text-[#f59e0b]" : "text-[#ef4444]"
                      }>{signal.ati_decision.verdict}</dd>
                      <dt className="text-[#6b7280]">Regime</dt><dd>{signal.ati_decision.regime}</dd>
                      <dt className="text-[#6b7280]">Lot ×</dt><dd>{(signal.ati_decision.lot_multiplier * 100).toFixed(0)}%</dd>
                      <dt className="text-[#6b7280]">Health</dt><dd>{signal.ati_decision.health_score}/100</dd>
                    </dl>
                    {signal.ati_decision.reasons?.length > 0 && (
                      <ul className="mt-2 pl-4 list-disc text-[11px] text-[#9ca3af] space-y-0.5">
                        {signal.ati_decision.reasons.slice(0, 3).map((r, i) => <li key={i}>{r}</li>)}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

export const SignalsTable = ({ signals, emptyLabel }) => {
  return (
    <div className="vx-card overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="signals-table">
          <thead className="bg-[#0a0e1a] border-b border-[#1f2937]">
            <tr className="text-left">
              <th className="w-8"></th>
              <th className="py-2 px-2 vx-label">Time</th>
              <th className="py-2 px-2 vx-label">Symbol</th>
              <th className="py-2 px-2 vx-label">TF</th>
              <th className="py-2 px-2 vx-label">Pattern</th>
              <th className="py-2 px-2 vx-label">Dir</th>
              <th className="py-2 px-2 vx-label text-right">Entry</th>
              <th className="py-2 px-2 vx-label text-right">SL</th>
              <th className="py-2 px-2 vx-label text-right">TP</th>
              <th className="py-2 px-2 vx-label text-right">Lot</th>
              <th className="py-2 px-2 vx-label text-right">Max Loss</th>
              <th className="py-2 px-2 vx-label text-center">Conf.</th>
              <th className="py-2 px-2 vx-label">Decision</th>
              <th className="py-2 px-2 vx-label">Status</th>
            </tr>
          </thead>
          <tbody>
            {signals.length === 0 ? (
              <tr>
                <td colSpan={14} className="px-6 py-10 text-center text-[#6b7280] font-mono text-sm">
                  {emptyLabel || "No signals yet. Connect your EA to start."}
                </td>
              </tr>
            ) : (
              signals.map((s, i) => <SignalRow key={s.id} signal={s} index={i} />)
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
